import {
  BACK_TO_ROOMS_LABEL,
  COPY_MARKDOWN_DONE,
  COPY_MARKDOWN_FAILED,
  COPY_MARKDOWN_LABEL,
  LINK_EXPIRED_LABEL,
  LINK_EXPIRED_TITLE,
} from '@/components/admin/entretiens/entretien-labels';
import { TranscriptHead } from '@/components/admin/entretiens/transcript-head';
import { TranscriptReader } from '@/components/admin/entretiens/transcript-reader';
import { Button } from '@/components/ui/button';
import { PmsSpinner } from '@/components/ui/pms-spinner';
import { Tooltip } from '@/components/ui/tooltip';
import { useAdminSession, useRejectOn401 } from '@/lib/admin-session';
import { ApiError, fetchChatRoomMarkdown, fetchChatRoomTranscript } from '@/lib/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';

/**
 * S10 - one interview, in full (chat spec §12, issue #11).
 *
 * **This screen is where the deliverable of the cycle is read.** The pitch wants
 * the transcript of an interview Patrick conducted, and it names what it is
 * looking for in it: what he opens with, how many turns before the first matter
 * arrives, the rhythm at which he goes deeper, and what he does when it stalls.
 * So this is a reading instrument, not a dashboard - the head carries five
 * counts and the rest of the page is the thread, whole and verbatim.
 *
 * **It answers after the links are dead, and that is the whole point.** §10
 * decided that the link expires and the transcript is kept, and this route is
 * what makes that decision livable: nothing here consults the entry guard as a
 * gate, only as a fact to display beside the reference. An operator who comes
 * back a week later reads the interview exactly as it happened.
 *
 * The markdown export goes to the CLIPBOARD rather than to a file, because what
 * the cycle does with a transcript is paste it into a ticket - the shape
 * `scripts/dump-session.ts` was built for. The document is rendered SERVER SIDE
 * by `chat/transcript.ts`, off the same reading this page renders, so the pasted
 * version and the screen cannot disagree about a figure.
 */

export const Route = createFileRoute('/admin/entretiens/$roomId')({
  component: TranscriptPage,
});

function TranscriptPage() {
  const { email } = useAdminSession();
  const { roomId } = Route.useParams();

  const transcript = useQuery({
    queryKey: ['chat-transcript', email, roomId],
    queryFn: () => fetchChatRoomTranscript(roomId),
    retry: false,
    /*
      NO polling, unlike the session panel of `/admin/journal`.

      That panel re-asks on its own because it is used to watch a session being
      played from a second tab. This one is not: the conversation is watched by
      the interviewer, in the chat itself, and an interview being held is a
      conversation between two people whose transcript nobody is reading over
      their shoulder. A refetch every few seconds would also move the ground
      under somebody who is reading a rhythm down a column of stamps.
    */
  });

  const rejected = useRejectOn401(transcript.error);
  const missing = transcript.error instanceof ApiError && transcript.error.status === 404;

  const copy = useMutation({
    mutationFn: async () => {
      const markdown = await fetchChatRoomMarkdown(roomId);
      /*
        The clipboard write is INSIDE the mutation, so a browser that refuses it
        turns into a visible failure rather than a button that silently did
        nothing. That is the opposite call from the two link rows of the creation
        panel, and the difference is what the fallback is: there, the URL sits in
        a selectable input beside the button; here the document is a few thousand
        characters that exist nowhere on the page.
      */
      await navigator.clipboard.writeText(markdown);
    },
  });

  return (
    <section className="space-y-5">
      <div className="space-y-2">
        <Link
          to="/admin/entretiens"
          className="inline-flex items-center gap-1 font-semibold text-bleu-nuit text-sm"
        >
          {BACK_TO_ROOMS_LABEL}
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1">
            {/*
              The REFERENCE is the heading, and not the identifier: it is what
              the guest quotes to ask for an erasure (§12), so it is the string
              an operator matches against a message she was sent. The identifier
              stays in the URL, where it is addressing and not naming.
            */}
            <h1 className="font-bold text-3xl text-bleu-nuit tabular-nums tracking-wide">
              {transcript.data?.room.reference ?? '…'}
            </h1>
            {transcript.data?.room.linkExpired === true ? (
              <Tooltip label={LINK_EXPIRED_TITLE}>
                <p className="text-gray-500 text-sm">
                  {LINK_EXPIRED_LABEL} - {LINK_EXPIRED_TITLE}
                </p>
              </Tooltip>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => copy.mutate()}
              disabled={copy.isPending || transcript.data === undefined}
            >
              {copy.isSuccess ? COPY_MARKDOWN_DONE : COPY_MARKDOWN_LABEL}
            </Button>
          </div>
        </div>
        {copy.isError ? <p className="text-gray-700 text-sm">{COPY_MARKDOWN_FAILED}</p> : null}
      </div>

      {transcript.isPending ? (
        <div className="flex justify-center py-12">
          <PmsSpinner label="Chargement du transcript" />
        </div>
      ) : null}

      {/* 404 is « cette salle n'existe pas » and everything else is « la requête
          a échoué » - two different things to do about it, so two sentences. */}
      {missing ? <p className="text-gray-700">Aucun entretien sous cet identifiant.</p> : null}
      {transcript.isError && !missing && !rejected ? (
        <p className="text-gray-700">Le transcript n’a pas pu être chargé.</p>
      ) : null}

      {transcript.data ? (
        <>
          <TranscriptHead transcript={transcript.data} />
          <TranscriptReader entries={transcript.data.entries} />
        </>
      ) : null}
    </section>
  );
}
